/**
 * =============================================================================
 * Preis-Helper: eine Quelle der Wahrheit für die Preisanzeige (a-055 / a-055b)
 * =============================================================================
 * D1-Produktpreise (price.cents, unitPriceCents im Warenkorb) sind NETTO.
 *
 * Anzeigemodus (priceDisplayMode):
 *   'gross' -> Kauf-Modus (checkout_mode = stripe_checkout), außerhalb OCI.
 *              Anzeige inkl. MwSt. = Zahlbetrag Stripe.
 *   'net'   -> Anfrage-Modus (Default) und OCI/ES2000-PunchOut (?mode=oci).
 *              Anzeige zzgl. MwSt. = ES2000-Übergabewerte (netto).
 *
 * Rundung je Stück (identisch zu functions/api/_lib/stripe-checkout.js):
 *   brutto je Stück = round(netto * (1 + rate)); Zeile = Stück * brutto je Stück.
 *
 * MwSt.-Satz: tenant.vatRate, sonst tenant.vat_rate, sonst tenant.settings.vat_rate
 * (dieselbe Quelle wie functions/api/_lib/vat.js), Fallback 19 %.
 *
 * Bewusst ignoriert: product.price.formatted (Serverwert). Alle Anzeigen berechnen
 * den Betrag aus price.cents im Client, damit Netto/Brutto einheitlich bleibt.
 */

import { isStripeCheckout } from './checkout-mode.js';

export const DEFAULT_VAT_RATE = 0.19;

/** MwSt.-Satz des Tenants (Dezimal, z. B. 0.07). Ungültig/fehlend -> DEFAULT_VAT_RATE. */
export function vatRateOf(tenant) {
  const raw = tenant?.vatRate ?? tenant?.vat_rate ?? tenant?.settings?.vat_rate;
  const n = Number(raw);
  return raw !== undefined && raw !== null && raw !== '' && Number.isFinite(n) && n >= 0 && n < 1
    ? n
    : DEFAULT_VAT_RATE;
}

/** true, wenn die aktuelle Seite im OCI/ES2000-PunchOut-Modus ist (?mode=oci). */
export function isOciUrl() {
  if (typeof window === 'undefined' || !window.location?.href) return false;
  try {
    return new URL(window.location.href).searchParams.get('mode') === 'oci';
  } catch {
    return false;
  }
}

/** 'gross' im Kauf-Modus (außer OCI), sonst 'net'. */
export function priceDisplayMode(tenant, { oci = isOciUrl() } = {}) {
  if (oci) return 'net';
  return isStripeCheckout(tenant) ? 'gross' : 'net';
}

/** Brutto-Cent je Stück aus Netto-Cent. */
export function grossUnitCents(netCents, vatRate = DEFAULT_VAT_RATE) {
  const net = Math.max(0, Math.round(Number(netCents) || 0));
  return Math.round(net * (1 + vatRate));
}

/** Anzuzeigender Stückpreis in Cent für den Modus. */
export function displayUnitCents(netCents, mode, vatRate = DEFAULT_VAT_RATE) {
  return mode === 'gross' ? grossUnitCents(netCents, vatRate) : Math.max(0, Math.round(Number(netCents) || 0));
}

/**
 * Summen eines Warenkorbs (unitPriceCents = Netto je Stück).
 * netCents = Nettosumme, vatCents = MwSt., grossCents = Zahlsumme (Stripe-Parität).
 */
export function computeGrossTotals(items = [], vatRate = DEFAULT_VAT_RATE) {
  let netCents = 0;
  let grossCents = 0;
  for (const item of Array.isArray(items) ? items : []) {
    const qty = Math.max(1, Number(item?.quantity) || 1);
    const unitNet = Math.max(0, Number(item?.unitPriceCents) || 0);
    netCents += qty * unitNet;
    grossCents += qty * grossUnitCents(unitNet, vatRate);
  }
  return { netCents, vatCents: grossCents - netCents, grossCents };
}

/** Anzuzeigende Gesamtsumme in Cent für den Modus. */
export function displayTotalCents(items, mode, vatRate = DEFAULT_VAT_RATE) {
  const t = computeGrossTotals(items, vatRate);
  return mode === 'gross' ? t.grossCents : t.netCents;
}

/** MwSt.-Hinweis passend zum Modus, z. B. "inkl. 19% MwSt." oder "zzgl. 19% MwSt.". */
export function vatNoticeFor(mode, vatRate = DEFAULT_VAT_RATE) {
  const pct = Math.round(vatRate * 100);
  return mode === 'gross' ? `inkl. ${pct}% MwSt.` : `zzgl. ${pct}% MwSt.`;
}
